import React, { useState, useEffect } from "react";
import { API_URLS } from "../../apiUrl";
import Pagination from '@mui/material/Pagination';
import axiosInstance from "../../components/axiosInstance";

type FormData = {
    type_flag: string;
    page: number;
    limit: number;
};

const PropertyStructure: React.FC = () => {
    const [errors, setErrors] = useState<any>({});
    const [tableData, setTableData] = useState<any[]>([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [loading, setLoading] = useState(false);

    const [inputData, setInputData] = useState({ page: 1, limit: 50 });
    const [totalItems, setTotalItems] = useState(0);
    const [itemsPerPage] = useState(50);

    const [formData, setFormData] = useState<FormData>({
        type_flag: "",
        page: inputData.page,
        limit: inputData.limit,
    });

    const handleChange = (event: any) => {
        const { name, value } = event.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const validateForm = (): boolean => {
        const newErrors: any = {};
        if (!formData.type_flag.trim()) newErrors.type_flag = "Description name is required";

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };

    const fetchData = async () => {
        if (formData.type_flag) {
            setLoading(true);
            try {
                const response = await axiosInstance.post(`${API_URLS.PropertyStructure}`, {
                    ...formData,
                    page: inputData.page,
                });

                if (response.data.data.data) {
                    setTableData(response.data.data.data);
                    setTotalItems(response.data.data.total);
                } else {
                    setTableData([]);
                }
            } catch (error) {
                console.error("Failed to fetch data:", error);
            } finally {
                setLoading(false);
            }
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (validateForm()) {
            fetchData();
        }
    };

    const filteredData = tableData.filter((item: any) =>
        item.type_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.status.toString().includes(searchQuery.toLowerCase())
    );

    const handlePageChange = (_event: any, value: number) => {
        setInputData((prevData) => ({
            ...prevData,
            page: value,
        }));
    };

    useEffect(() => {
        fetchData();
    }, [inputData.page, formData]);

    const totalPages = Math.ceil(totalItems / itemsPerPage);

    return (
        <>
            <div className="max-w-full mx-auto">
                <div className="flex flex-col gap-5">
                    <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">Property Structure</h1>

                    <div className="p-6 bg-white rounded-lg shadow-lg dark:bg-gray-800">
                        <div className="flex flex-col gap-5">
                            <form onSubmit={handleSubmit}>
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                                    <div>
                                        <label
                                            htmlFor="description_name"
                                            className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                                        >
                                            Select Structure Type
                                        </label>
                                        <div className="flex">
                                            <select
                                                id="type_flag"
                                                name="type_flag"
                                                value={formData.type_flag}
                                                onChange={handleChange}
                                                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-0 focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                                            >
                                                <option value="">Select Type</option>
                                                <option value="floor">Floor</option>
                                                <option value="roof">Roof</option>
                                                <option value="wall">Wall</option>
                                            </select>
                                        </div>
                                        {errors.type_flag && (
                                            <p className="text-sm text-red-600 mt-1">{errors.type_flag}</p>
                                        )}
                                    </div>
                                </div>
                            </form>

                            {/* Data Table */}
                            {filteredData.length > 0 && (
                                <div className="container mx-auto">
                                    <div className="flex gap-5 mb-4">
                                        <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">{`Property Type List (${filteredData[0].type_flag})`}</h1>
                                    </div>

                                    <div className="overflow-x-auto bg-white rounded-lg border border-gray-200 dark:border-gray-800">
                                        <table className="min-w-full table-auto">
                                            <thead className="bg-gray-100 border-b dark:border-gray-600 dark:bg-gray-700">
                                                <tr>
                                                    <th className="py-3 px-6 text-left text-sm font-medium text-gray-600 dark:text-gray-100">Structure Type</th>
                                                    <th className="py-3 px-6 text-left text-sm font-medium text-gray-600 dark:text-gray-100">Created At</th>
                                                    <th className="py-3 px-6 text-left text-sm font-medium text-gray-600 dark:text-gray-100">Updated At</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {loading ? (
                                                    <tr>
                                                        <td colSpan={3} className="py-3 px-6 text-center">Loading...</td>
                                                    </tr>
                                                ) : filteredData.length === 0 ? (
                                                    <tr>
                                                        <td colSpan={3} className="py-3 px-6 text-center dark:bg-gray-800">No results found</td>
                                                    </tr>
                                                ) : (
                                                    filteredData.map((item: any, index) => (
                                                        <tr key={index} className="border-b dark:border-gray-700 hover:bg-gray-50">
                                                            <td className="py-3 px-6 text-sm text-gray-800 dark:text-gray-200 dark:bg-gray-600">{item.type_name || 'N/A'}</td>
                                                            <td className="py-3 px-6 text-sm text-gray-800 dark:text-gray-200 dark:bg-gray-600">
                                                                {item.created_at ? new Date(item.created_at).toLocaleString() : new Date().toLocaleString()}
                                                            </td>
                                                            <td className="py-3 px-6 text-sm text-gray-800 dark:text-gray-200 dark:bg-gray-600">
                                                                {item.updated_at ? new Date(item.updated_at).toLocaleString() : new Date().toLocaleString()}
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    {filteredData.length > 0 && (
                                        <div className="flex justify-center mt-4">
                                            <Pagination
                                                count={totalPages}
                                                page={inputData.page}
                                                onChange={handlePageChange}
                                                color="primary"
                                            />
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default PropertyStructure;